import React, { useState, useEffect, useRef } from 'react';
import { palettes, Tooltip, useFlashMessage } from '@indigo-ag/component-module';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import copyToClipboard from 'clipboard-copy';
import i18next from 'i18next';

import { createStylesHook } from 'shared/hooks/createStylesHook';

interface CopyToClipboardButtonProps {
  valueToCopy: string;
  isTextVisible?: boolean;
  tooltipTitle?: string;
}

const useStyles = createStylesHook({
  containerSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  copyButtonStyles: {
    marginLeft: 5
  },
  textStyles: {
    color: palettes.coastal.core.mediumBlue
  }
});

export const CopyToClipboardButton: React.FunctionComponent<CopyToClipboardButtonProps> = ({
  valueToCopy,
  isTextVisible = false,
  tooltipTitle
}) => {
  /**
   * State
   */
  const [isCopied, setIsCopied] = useState(false);

  /**
   * Styles
   */
  const classes = useStyles();

  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { addFlashMessage } = useFlashMessage();

  const copyToClipboardAction = (copiedValue: string) => {
    setIsCopied(true);
    copyToClipboard(copiedValue)
      .then(() => {
        addFlashMessage({
          content: i18next.t('copiedToClipboard'),
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setTimeout(() => mountedRef.current && setIsCopied(false), 3000));
  };

  return (
    <>
      {isCopied ? (
        <div className={classes.containerSection}>
          {isTextVisible ? <span className={classes.textStyles}>{i18next.t('copied')}</span> : null}
          <FontAwesomeIcon
            icon={faCheckCircle}
            className={classes.copyButtonStyles}
            color={palettes.coastal.accent.green}
          />
        </div>
      ) : (
        <Tooltip title={!tooltipTitle ? i18next.t('copyToClipboard') : tooltipTitle} placement="top-start">
          <div onClick={() => copyToClipboardAction(valueToCopy)} className={classes.containerSection}>
            {isTextVisible ? <span className={classes.textStyles}>{i18next.t('copy')}</span> : null}
            <FontAwesomeIcon
              icon={faCopy}
              className={classes.copyButtonStyles}
              color={palettes.coastal.core.mediumBlue}
            />
          </div>
        </Tooltip>
      )}
    </>
  );
};
